import {
    ALLOWED_BANNER_MIME_TYPES,
    BANNER_DIMENSIONS,
    MAX_BANNER_BYTES,
} from '@api/banners';

import type {
    BannerAddition,
    BannerListItem,
    BannerUpdate,
    BannersBulkRequest,
} from '@api/banners';
import type { BannerScope } from '@api/banners';
import type {
    BannerAdditionDraft,
    BannerEditorDraft,
    BannerExistingDraft,
} from './BannerEditor.types';

type FileValidation = { ok: true } | { ok: false; reason: string };

type ImageDimensions = { width: number; height: number };

const probeImageDimensions = (dataUrl: string): Promise<ImageDimensions> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () =>
            resolve({ width: image.naturalWidth, height: image.naturalHeight });
        image.onerror = () => reject(new Error('Could not decode image'));
        image.src = dataUrl;
    });

const checkDimensions = ({ width, height }: ImageDimensions): FileValidation => {
    const { minWidth, minHeight, aspectWidth, aspectHeight, aspectTolerance } = BANNER_DIMENSIONS;

    if (width < minWidth || height < minHeight) {
        return {
            ok: false,
            reason: `Image must be at least ${minWidth}×${minHeight}px (got ${width}×${height})`,
        };
    }

    const targetRatio = aspectWidth / aspectHeight;
    const actualRatio = width / height;
    const lowerBound = targetRatio * (1 - aspectTolerance);
    const upperBound = targetRatio * (1 + aspectTolerance);
    if (actualRatio < lowerBound || actualRatio > upperBound) {
        const tolerancePercent = Math.round(aspectTolerance * 100);
        return {
            ok: false,
            reason: `Image must have a ${aspectWidth}:${aspectHeight} aspect ratio (±${tolerancePercent}%); got ${width}×${height}`,
        };
    }

    return { ok: true };
};

const checkFileMetadata = (file: File): FileValidation => {
    if (!ALLOWED_BANNER_MIME_TYPES.includes(file.type as never)) {
        return { ok: false, reason: `Unsupported file type: ${file.type || 'unknown'}` };
    }
    if (file.size > MAX_BANNER_BYTES) {
        const mb = (MAX_BANNER_BYTES / (1024 * 1024)).toFixed(1);
        return { ok: false, reason: `File exceeds the ${mb}MB limit` };
    }
    return { ok: true };
};

export const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });

export type ValidationOutcome =
    /** File is unusable end-to-end (wrong MIME, oversize, undecodable). */
    | { kind: 'hardFail'; reason: string }
    /** File decoded but doesn't meet dimension/ratio rules; still surfaced in the editor so users can see what was rejected. */
    | { kind: 'invalid'; dataUrl: string; mimeType: string; reason: string }
    | { kind: 'ok'; dataUrl: string; mimeType: string };

export const validateAndReadBannerFile = async (file: File): Promise<ValidationOutcome> => {
    const metaResult = checkFileMetadata(file);
    if (!metaResult.ok) return { kind: 'hardFail', reason: metaResult.reason };

    let dataUrl: string;
    try {
        dataUrl = await readFileAsDataUrl(file);
    } catch {
        return { kind: 'hardFail', reason: 'Failed to read file' };
    }

    let dimensions: ImageDimensions;
    try {
        dimensions = await probeImageDimensions(dataUrl);
    } catch {
        return { kind: 'hardFail', reason: 'Could not decode image to verify dimensions' };
    }

    const dimsResult = checkDimensions(dimensions);
    if (!dimsResult.ok) {
        return { kind: 'invalid', dataUrl, mimeType: file.type, reason: dimsResult.reason };
    }

    return { kind: 'ok', dataUrl, mimeType: file.type };
};

export const hasInvalidAdditions = (draft: BannerEditorDraft): boolean =>
    draft.additions.some((entry) => Boolean(entry.invalidReason));

export const BANNER_REQUIREMENTS_SUMMARY = (() => {
    const { minWidth, minHeight, aspectWidth, aspectHeight, aspectTolerance } = BANNER_DIMENSIONS;
    return `${minWidth}×${minHeight}px minimum, ${aspectWidth}:${aspectHeight} aspect ratio (±${Math.round(
        aspectTolerance * 100
    )}%).`;
})();

export const buildInitialDraft = (banners: BannerListItem[]): BannerEditorDraft => ({
    existing: banners.map((item) => ({
        item,
        displayOrder: item.DisplayOrder,
        pendingDeletion: false,
    })),
    additions: [],
});

const orderedDraftEntries = (draft: BannerEditorDraft) => {
    const existingEntries = draft.existing
        .filter((entry) => !entry.pendingDeletion)
        .map((entry) => ({
            kind: 'existing' as const,
            displayOrder: entry.displayOrder,
            entry,
        }));
    const additionEntries = draft.additions.map((entry) => ({
        kind: 'addition' as const,
        displayOrder: entry.displayOrder,
        entry,
    }));
    return [...existingEntries, ...additionEntries].sort(
        (left, right) => left.displayOrder - right.displayOrder
    );
};

export const getVisibleOrder = (draft: BannerEditorDraft) => orderedDraftEntries(draft);

const reindex = (draft: BannerEditorDraft): BannerEditorDraft => {
    const ordered = orderedDraftEntries(draft);
    const additions: BannerAdditionDraft[] = [];
    const existingById = new Map<number, BannerExistingDraft>();
    ordered.forEach(({ kind, entry }, index) => {
        if (kind === 'existing') {
            existingById.set(entry.item.BannerID, { ...entry, displayOrder: index });
        } else {
            additions.push({ ...entry, displayOrder: index });
        }
    });
    const existing = draft.existing.map((entry) =>
        entry.pendingDeletion ? entry : existingById.get(entry.item.BannerID) ?? entry
    );
    return { existing, additions };
};

type OrderedEntry = ReturnType<typeof orderedDraftEntries>[number];

const matchesExisting = (orderedEntry: OrderedEntry, bannerId: number) =>
    orderedEntry.kind === 'existing' && orderedEntry.entry.item.BannerID === bannerId;

const matchesAddition = (orderedEntry: OrderedEntry, tempId: string) =>
    orderedEntry.kind === 'addition' && orderedEntry.entry.tempId === tempId;

export const moveItem = (
    draft: BannerEditorDraft,
    key: { kind: 'existing'; bannerId: number } | { kind: 'addition'; tempId: string },
    direction: -1 | 1
): BannerEditorDraft => {
    const ordered = orderedDraftEntries(draft);
    const currentIndex = ordered.findIndex((entry) =>
        key.kind === 'existing'
            ? matchesExisting(entry, key.bannerId)
            : matchesAddition(entry, key.tempId)
    );
    const swapIndex = currentIndex + direction;
    if (currentIndex < 0 || swapIndex < 0 || swapIndex >= ordered.length) {
        return draft;
    }
    const current = ordered[currentIndex];
    const neighbor = ordered[swapIndex];

    const swapDisplayOrder = <Entry extends { displayOrder: number }>(
        entry: Entry,
        isCurrent: boolean,
        isNeighbor: boolean
    ): Entry => {
        if (isCurrent) return { ...entry, displayOrder: neighbor.displayOrder };
        if (isNeighbor) return { ...entry, displayOrder: current.displayOrder };
        return entry;
    };

    const next: BannerEditorDraft = {
        existing: draft.existing.map((entry) =>
            swapDisplayOrder(
                entry,
                current.kind === 'existing' && entry.item.BannerID === current.entry.item.BannerID,
                neighbor.kind === 'existing' && entry.item.BannerID === neighbor.entry.item.BannerID
            )
        ),
        additions: draft.additions.map((entry) =>
            swapDisplayOrder(
                entry,
                current.kind === 'addition' && entry.tempId === current.entry.tempId,
                neighbor.kind === 'addition' && entry.tempId === neighbor.entry.tempId
            )
        ),
    };
    return reindex(next);
};

export const addAddition = (
    draft: BannerEditorDraft,
    addition: Omit<BannerAdditionDraft, 'displayOrder'>
): BannerEditorDraft => {
    const visibleCount = orderedDraftEntries(draft).length;
    return reindex({
        ...draft,
        additions: [...draft.additions, { ...addition, displayOrder: visibleCount }],
    });
};

export const removeAddition = (
    draft: BannerEditorDraft,
    tempId: string
): BannerEditorDraft =>
    reindex({
        ...draft,
        additions: draft.additions.filter((entry) => entry.tempId !== tempId),
    });

export const togglePendingDeletion = (
    draft: BannerEditorDraft,
    bannerId: number
): BannerEditorDraft => {
    const next = {
        ...draft,
        existing: draft.existing.map((entry) =>
            entry.item.BannerID === bannerId
                ? { ...entry, pendingDeletion: !entry.pendingDeletion }
                : entry
        ),
    };
    return reindex(next);
};

const stripDataUrlPrefix = (dataUrl: string): string => {
    const commaIndex = dataUrl.indexOf(',');
    return commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
};

const buildAdditions = (draft: BannerEditorDraft): BannerAddition[] =>
    draft.additions.map((entry) => ({
        imageBase64: stripDataUrlPrefix(entry.dataUrl),
        mimeType: entry.mimeType,
        displayOrder: entry.displayOrder,
    }));

const buildUpdates = (draft: BannerEditorDraft): BannerUpdate[] =>
    draft.existing
        .filter(
            (entry) =>
                !entry.pendingDeletion && entry.displayOrder !== entry.item.DisplayOrder
        )
        .map((entry) => ({ bannerId: entry.item.BannerID, displayOrder: entry.displayOrder }));

const buildDeletions = (draft: BannerEditorDraft): number[] =>
    draft.existing
        .filter((entry) => entry.pendingDeletion)
        .map((entry) => entry.item.BannerID);

export const hasPendingChanges = (draft: BannerEditorDraft): boolean =>
    draft.additions.length > 0 ||
    draft.existing.some(
        (entry) => entry.pendingDeletion || entry.displayOrder !== entry.item.DisplayOrder
    );

export const buildBulkRequest = (
    draft: BannerEditorDraft,
    scope: BannerScope,
    companyId?: number
): BannersBulkRequest => ({
    scope,
    companyId,
    additions: buildAdditions(draft),
    updates: buildUpdates(draft),
    deletions: buildDeletions(draft),
});
